import type { Board } from "./board";
import { Mob } from "./mob";
import { Portal2 } from "./things/portal2/portal2";
import type { Thing } from "./thing";

export enum HostilityFactor {
	MOBS_KILLED = "MOBS_KILLED",
	MOBS_SOLIDIFIED = "MOBS_SOLIDIFIED",
	PORTALS_DESTROYED = "PORTALS_DESTROYED",
	PORTALS_ALIVE = "PORTALS_ALIVE",
	MOBS_ALIVE = "MOBS_ALIVE",
	TURN_COUNT = "TURN_COUNT",
}

const STARTER_WEAPON_KEYS = ["SolidifierShot", "ShortSolidifierShot", "CryoShot", "AirBlastShot"];

export class HostilityCalculator {
	private board: Board;
	private factors: Map<HostilityFactor, number> = new Map();

	private turnCount = 0;
	private portalsDestroyed = 0;
	private portalsAlive = 0;
	private mobsSolidified = 0;
	private mobsKilled = 0;
	private mobsAlive = 0;

	constructor(board: Board) {
		this.board = board;
	}

	setFactor(factor: HostilityFactor, value: number): void {
		this.factors.set(factor, value);
	}

	setFactorByPercentage(factor: HostilityFactor, percentage: number): void {
		this.setFactor(factor, 1 - percentage / 100);
	}

	getFactor(factor: HostilityFactor): number {
		return this.factors.get(factor) ?? 1;
	}

	calculate(): number {
		this.turnCount = this.board.turnCount;
		this.readStats();
		this.scanBoardEntities();
		return this.calculateHostility();
	}

	private calculateHostility(): number {
		const hostility =
			1 -
			Math.pow(this.getFactor(HostilityFactor.TURN_COUNT), this.turnCount) *
				Math.pow(this.getFactor(HostilityFactor.PORTALS_DESTROYED), this.portalsDestroyed) *
				Math.pow(this.getFactor(HostilityFactor.MOBS_KILLED), this.mobsKilled) *
				Math.pow(this.getFactor(HostilityFactor.MOBS_SOLIDIFIED), this.mobsSolidified) *
				Math.pow(this.getFactor(HostilityFactor.MOBS_ALIVE), this.mobsAlive) *
				Math.pow(this.getFactor(HostilityFactor.PORTALS_ALIVE), this.portalsAlive);

		return Math.max(hostility, 0);
	}

	private readStats(): void {
		const stats = this.board.stats;
		this.mobsKilled = this.sumMap(stats.mobsKilledByType);
		this.portalsDestroyed = stats.spawnersDestroyed;

		let starterKills = 0;
		for (const key of STARTER_WEAPON_KEYS) {
			starterKills += stats.killsByWeaponKey.get(key) ?? 0;
		}
		this.mobsSolidified = starterKills;
	}

	private scanBoardEntities(): void {
		this.mobsAlive = 0;
		this.portalsAlive = 0;
		for (const t of this.board.getPhysicalThings()) {
			this.updateCounters(t);
		}
	}

	private updateCounters(t: Thing): void {
		if (t instanceof Mob) this.mobsAlive++;
		else if (t instanceof Portal2) this.portalsAlive++;
	}

	private sumMap(map: Map<string, number>): number {
		let total = 0;
		for (const v of map.values()) {
			total += v;
		}
		return total;
	}
}
